import { useEffect, useState } from "react";

/**
 * Drives the 10s auto-refresh loop on the Organizations list while any org is
 * still in `Creating` state.
 *
 * Usage:
 *   const { countdown } = usePollOrganizations({
 *     enabled: hasCreatingOrgs,
 *     intervalMs: 10000,
 *     onPoll: stableCallback, // wrap in useCallback or a ref
 *   });
 *
 * `onPoll` must be stable across renders — otherwise the internal effect
 * restarts the interval on every parent render. The typical pattern in the
 * parent is to stash mutable inputs in a ref and keep `onPoll` empty-deps.
 */
export function usePollOrganizations({ enabled, intervalMs = 10000, onPoll }) {
  const totalSeconds = Math.max(1, Math.floor(intervalMs / 1000));
  const [countdown, setCountdown] = useState(totalSeconds);

  useEffect(() => {
    if (!enabled) {
      setCountdown(totalSeconds);
      return undefined;
    }

    setCountdown(totalSeconds);

    const tick = setInterval(() => {
      setCountdown((c) => (c > 1 ? c - 1 : totalSeconds));
    }, 1000);

    const poll = setInterval(() => {
      onPoll();
      setCountdown(totalSeconds);
    }, intervalMs);

    return () => {
      clearInterval(tick);
      clearInterval(poll);
    };
  }, [enabled, intervalMs, onPoll, totalSeconds]);

  return { countdown };
}

export default usePollOrganizations;
